import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { type Me, meQuery, resolveWorkspace } from '@/lib/queries'

const searchSchema = z.object({
  connection: z.uuid().optional(),
  error: z.string().optional(),
})

type CallbackSearch = z.infer<typeof searchSchema>

/**
 * Google hands control back here rather than to a workspace URL: at that point
 * the only place the tenant exists is the signed OAuth state the API just
 * verified, so the slug has to be resolved from the session instead.
 */
export const Route = createFileRoute('/connect/callback')({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => {
    const parsed = searchSchema.safeParse(search)
    return parsed.success ? parsed.data : {}
  },
  beforeLoad: async ({ context, location, search }) => {
    let me: Me
    try {
      me = await context.queryClient.ensureQueryData(meQuery)
    } catch {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }

    if (!search.connection) return

    // The API set the active organization from the OAuth state, so the session
    // already points at the workspace that started this.
    const target =
      me.organizations.find((organization) => organization.id === me.activeOrganizationId) ??
      resolveWorkspace(me)
    if (!target) return

    await Promise.all([
      context.queryClient.invalidateQueries({ queryKey: ['connections'] }),
      context.queryClient.invalidateQueries({ queryKey: ['me'] }),
    ])

    throw redirect({
      to: '/$org/settings/connections',
      params: { org: target.slug },
      search: { connection: search.connection },
      replace: true,
    })
  },
  pendingComponent: Resolving,
  component: ConnectCallbackPage,
})

const REASONS: Record<string, string> = {
  access_denied: 'You cancelled at the Google prompt, so nothing was connected.',
  invalid_state:
    'That link expired or did not match this browser. Start the connection again from the workspace.',
  missing_refresh_token:
    'Google did not hand back a refresh token. Remove SEO Plum under your Google account permissions, then connect again so it issues a new one.',
  google_not_configured: 'Google sign-in is not configured on this deployment yet.',
}

function ConnectCallbackPage() {
  const { connection, error } = Route.useSearch()
  const { data: me } = useQuery(meQuery)
  const org = me ? resolveWorkspace(me)?.slug : undefined

  // The happy path redirects out of beforeLoad, so reaching the component with
  // a connection id means the redirect is still in flight.
  if (connection && !error) return <Resolving />

  return (
    <Shell>
      <p className="text-[15px] font-medium">Google was not connected</p>
      <p className="max-w-sm text-[13px] text-muted-foreground">
        {(error && REASONS[error]) ?? 'The connection did not complete.'}
      </p>
      {error && (
        <p className="font-mono text-[11px] text-muted-foreground">
          <span className="text-border-strong" aria-hidden>
            ❯{' '}
          </span>
          {error}
        </p>
      )}
      <div className="pt-2">
        {org ? (
          <Button asChild size="sm" variant="outline">
            <Link to="/$org/settings/connections" params={{ org }}>
              Try again
            </Link>
          </Button>
        ) : (
          <Button asChild size="sm" variant="outline">
            <Link to="/">Back to your workspace</Link>
          </Button>
        )}
      </div>
    </Shell>
  )
}

function Resolving() {
  return (
    <Shell>
      <p className="font-mono text-[13px] text-muted-foreground">
        <span className="text-plum" aria-hidden>
          ❯{' '}
        </span>
        finishing connection
        <span className="caret ml-1 align-baseline text-muted-foreground/70" />
      </p>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3 bg-canvas px-6 text-center">
      <Logo className="pb-4 opacity-70" />
      {children}
    </div>
  )
}
